import {
  AccountBalance,
  accountBalanceAtom,
  appAuthAtom,
  AppAuthState,
  appEventDispatcher,
  AppEvents,
  AuthState,
  ConnectionState,
  currencyExchangeRateRTAtom,
  CurrencyExchangeRates,
  debugObjectsAtom,
  eStreamSessionIdAtom,
  EStreamTradeDTO,
  InstrumentStoreItem,
  inventoryDetailsAtom,
  InventoryDetailsMaybe,
  ObjectSet,
  SSESource,
  SubscribeOptions,
  UserStreamMessage,
} from '@eih-netbroker/common';
import { nanoid } from 'nanoid';
import { instrumentStore } from '../../data/instrumentStore';
import { store } from '../../state/store';
import { createLogger } from '../../utils/logger';
import MultiBrowserNetBrokerConfig = AppEvents.MultiBrowserNetBrokerConfig;

//
// PRIVATE TYPES
//

enum SSEManagerEvent {
  ReportForDuty = 'reportForDuty',
  ReportForDutyAnswer = 'ReportForDutyAnswer',
  InstanceDestroy = 'destroy',
  LoginStarted = 'LoginStarted',
  Login = 'Login',
  Logout = 'Logout',
  EStreamConnectionStateUpdate = 'EStreamConnectionStateUpdate',
  EStreamTrades = 'EStreamTrades',
  EStreamSubscriptionSubscribe = 'EStreamSubscriptionSubscribe',
  EStreamSubscriptionUnsubscribe = 'EStreamSubscriptionUnsubscribe',
  InstrumentStoreRequest = 'InstrumentStoreRequest',
  InstrumentStoreResponse = 'InstrumentStoreResponse',
  UserStreamMessage = 'UserStreamMessage',
  PrimaryInstanceSelected = 'PrimaryInstanceSelected',
  UpdateUserActivity = 'UpdateUserActivity',
}

type SSEManagerBasePayload = {
  eventType: SSEManagerEvent;
};
type SSEMReportForDutyPayload = {
  eventType: SSEManagerEvent.ReportForDuty;
  myInstanceId: number;
};
type SSEMReportForDutyPayloadAnswer = {
  eventType: SSEManagerEvent.ReportForDutyAnswer;
  myInstanceId: number;
  authState: AppAuthState | null;
  eStreamSessionId: string | null;
  eStreamConnectionState: ConnectionState | null;
  userStreamConnectionState: ConnectionState | null;
  inventoryDetails: InventoryDetailsMaybe;
  accountBalance: Array<AccountBalance>;
  currencyExchangeRates: CurrencyExchangeRates;
};
type SSEMLoginStartedPayload = {
  eventType: SSEManagerEvent.LoginStarted;
  primaryInstanceId: number;
};
type SSEMPrimaryInstanceSelectedPayload = {
  eventType: SSEManagerEvent.PrimaryInstanceSelected;
  primaryInstanceId: number;
};
type SSEMDestroyPayload = {
  eventType: SSEManagerEvent.InstanceDestroy;
  myInstanceId: number;
  eStreamSubscriptions: Array<SubscribeOptions>;
};
type SSEMEStreamConnectionStateUpdatePayload = SSEManagerBasePayload & {
  eventType: SSEManagerEvent.EStreamConnectionStateUpdate;
  connectionState: ConnectionState;
  sseSource: SSESource;
};
type SSEMEStreamTradesPayload = SSEManagerBasePayload & {
  eventType: SSEManagerEvent.EStreamTrades;
  trades: Array<EStreamTradeDTO> | EStreamTradeDTO;
};
type SSEMInstrumentStoreRequestPayload = SSEManagerBasePayload & {
  eventType: SSEManagerEvent.InstrumentStoreRequest;
};
type SSEMInstrumentStoreResponsePayload = SSEManagerBasePayload & {
  eventType: SSEManagerEvent.InstrumentStoreResponse;
  store: Array<InstrumentStoreItem>;
};
type SSEMLoginPayload = SSEManagerBasePayload & {
  eventType: SSEManagerEvent.Login;
} & MultiBrowserNetBrokerConfig;
type SSEMEStreamSubscriptionChangePayload = SSEManagerBasePayload & {
  eventType: SSEManagerEvent.EStreamSubscriptionSubscribe | SSEManagerEvent.EStreamSubscriptionUnsubscribe;
  options: Array<SubscribeOptions>;
};
type SSEMUserStreamMessagePayload = SSEManagerBasePayload &
  UserStreamMessage & {
    eventType: SSEManagerEvent.UserStreamMessage;
  };
type BroadcastMessage<T extends SSEManagerBasePayload> = {
  source: 'netbroker';
  nbInstance: number;
  messageId: string;
  responseId?: string;
  payload: T;
};

class SSEManager {
  readonly instanceId = Math.floor(Math.random() * 9999999) + 1;

  private readonly logger = createLogger('SSEManager', 'debug');
  private channel = new BroadcastChannel('net-broker-sse-manager');

  private primaryInstanceSelected = false;
  private isInitialized = false;
  private isDestroyed = false;

  /**
   * Id of the primary NetBroker instance
   * @private
   */
  private _primarySSEManagerInstanceId?: number;

  private questions: Record<
    string,
    {
      resolve: (response: any) => void;
      reject: () => void;
      timeout: NodeJS.Timer;
      broadCasted: number;
      waitInMs: number;
      answers: Record<string, any>;
      firstAnswerOnly: boolean;
    }
  > = {};

  private instanceList: Array<number> = [];

  eStreamConnectionState: ConnectionState | null = null;
  userStreamConnectionState: ConnectionState | null = null;

  get isPrimaryInstanceSelected() {
    return this.primaryInstanceSelected;
  }

  get primarySSEManagerInstanceId() {
    return this._primarySSEManagerInstanceId;
  }

  private get authState() {
    return store.get(appAuthAtom);
  }

  get eStreamSessionId() {
    return store.get(eStreamSessionIdAtom);
  }

  private get inventoryDetails() {
    return store.get(inventoryDetailsAtom);
  }

  private get accountBalance() {
    return store.get(accountBalanceAtom).values();
  }

  private get currencyExchangeRates() {
    return store.get(currencyExchangeRateRTAtom);
  }

  private get isAuthenticated() {
    return store.get(appAuthAtom).isAuthenticated;
  }

  constructor() {
    this.channel.onmessage = this.onMessage.bind(this);
  }

  private async getOtherInstances(): Promise<Record<string, SSEMReportForDutyPayloadAnswer>> {
    return await this.ask<Record<string, SSEMReportForDutyPayloadAnswer>, false, SSEMReportForDutyPayload>(
      {
        eventType: SSEManagerEvent.ReportForDuty,
        myInstanceId: this.instanceId,
      },
      false,
    );
  }

  /**
   * Creates the broadcast channel
   */
  async initAndCheckIfWeNeedToLogin(): Promise<boolean | null> {
    if (this.isInitialized) {
      return null;
    }
    this.logger.info(`Initializing SSEManager`);

    this.isDestroyed = false;
    this.isInitialized = true;

    const allInstances = await this.getOtherInstances();
    this.instanceList = Object.keys(allInstances).map(Number);
    const loggedInInstance = Object.values(allInstances).find(instance => instance.authState?.isAuthenticated);
    if (
      loggedInInstance &&
      loggedInInstance.authState?.isAuthenticated &&
      loggedInInstance.authState.authStatus !== AuthState.SignOff &&
      loggedInInstance.authState.secrets.JwtToken &&
      loggedInInstance.authState.secrets.idToken &&
      loggedInInstance.eStreamConnectionState &&
      loggedInInstance.userStreamConnectionState
    ) {
      this.logger.info(`SSE init result is: another tab is already logged in: ${loggedInInstance.myInstanceId}`);
      this._primarySSEManagerInstanceId = loggedInInstance.myInstanceId;

      // there is a logged in instance, share the secrets with context and local store
      appEventDispatcher.dispatchEvent(
        new AppEvents.OtherInstanceLoggedInEvent({
          jwtToken: loggedInInstance.authState.secrets.JwtToken,
          idToken: loggedInInstance.authState.secrets.idToken,
          eStreamSessionId: loggedInInstance.eStreamSessionId!,
          eStreamConnectionState: loggedInInstance.eStreamConnectionState,
          userStreamConnectionState: loggedInInstance.userStreamConnectionState,
          inventoryDetails: loggedInInstance.inventoryDetails,
          accountBalance: loggedInInstance.accountBalance,
          currencyExchangeRates: loggedInInstance.currencyExchangeRates,
        }),
      );
      this.updateDebugData();
      return false;
    } else {
      this.logger.info(
        `SSE init result is: my instance id is ${this.instanceId}, others are [${this.instanceList.join(',')}]`,
      );
      this.updateDebugData();
      // continue with login, because we are not logged in any of the instances
      return true;
    }
  }

  async getInstrumentStore(): Promise<Array<InstrumentStoreItem> | null> {
    try {
      const response = await this.ask<SSEMInstrumentStoreResponsePayload, true, SSEMInstrumentStoreRequestPayload>(
        {
          eventType: SSEManagerEvent.InstrumentStoreRequest,
        },
        true,
        5000,
      );
      return response.store;
    } catch (err) {
      this.logger.error(`Failed to get instrument store`, err);
      return null;
    }
  }

  /**
   * Destroys the broadcast channel, should be called when the app is closed
   */
  destroy(eStreamSubscriptions: ObjectSet<SubscribeOptions>) {
    if (!this.isInitialized) {
      // SSEManager is not yet initialized
      return;
    }

    // send a broadcast message, that this instance is destroyed
    this.broadcast<SSEMDestroyPayload>(
      {
        eventType: SSEManagerEvent.InstanceDestroy,
        myInstanceId: this.instanceId,
        eStreamSubscriptions: eStreamSubscriptions.values(),
      },
      {},
    );
    this.channel.close();
    this.isInitialized = false;
    this.isDestroyed = true;
  }

  private broadcast<T extends SSEManagerBasePayload>(payload: T, options: { messageId?: string; responseId?: string }) {
    const wrapper: BroadcastMessage<T> = {
      source: 'netbroker',
      nbInstance: this.instanceId,
      messageId: options?.messageId || nanoid(),
      responseId: options?.responseId,
      payload,
    };
    // broadcasting is only possible, when the app hasn't been started destroying yet
    if (!this.isDestroyed) {
      this.channel.postMessage(wrapper);
    }
  }

  private ask<
    RESP extends ANSWER_TYPE extends true ? SSEManagerBasePayload : Record<string, SSEManagerBasePayload>,
    ANSWER_TYPE extends boolean,
    PAYLOAD_TYPE extends SSEManagerBasePayload,
  >(payload: PAYLOAD_TYPE, firstAnswerOnly: ANSWER_TYPE, waitInMs = 2000): Promise<RESP> {
    return new Promise<RESP>((resolve, reject) => {
      const messageId = nanoid();
      const broadCasted = Date.now();
      this.broadcast(payload, { messageId });
      const timeout = setTimeout(() => {
        if (firstAnswerOnly) {
          reject();
        } else {
          resolve(this.questions[messageId].answers as RESP);
        }
        delete this.questions[messageId];
      }, waitInMs);
      this.questions[messageId] = { broadCasted, resolve, reject, timeout, answers: {}, firstAnswerOnly, waitInMs };
    });
  }

  private async onMessage(event: MessageEvent<any>) {
    const data = event.data as BroadcastMessage<SSEManagerBasePayload>;
    if (event.origin !== window.location.origin || data.nbInstance === this.instanceId) {
      return;
    }
    this.logger.trace(`Received`, data);
    const messageId = data.messageId || undefined;

    // handle response to a question
    if (data.responseId) {
      const question = this.questions[data.responseId];
      if (question) {
        question.answers[data.nbInstance] = data.payload;
        if (question.firstAnswerOnly) {
          question.resolve(question.answers[data.nbInstance]);
          delete this.questions[data.responseId];
          return;
        }
      }
    }

    switch (data.payload.eventType) {
      case SSEManagerEvent.ReportForDuty:
        // answer for collecting instances questions that this instance exists and alive
        this.broadcast<SSEMReportForDutyPayloadAnswer>(
          {
            eventType: SSEManagerEvent.ReportForDutyAnswer,
            myInstanceId: this.instanceId,
            authState: this.authState,
            eStreamSessionId: this.eStreamSessionId || null,
            eStreamConnectionState: this.eStreamConnectionState,
            userStreamConnectionState: this.userStreamConnectionState,
            inventoryDetails: this.inventoryDetails,
            accountBalance: this.accountBalance,
            currencyExchangeRates: this.currencyExchangeRates,
          },
          { responseId: messageId },
        );
        break;
      case SSEManagerEvent.InstanceDestroy:
        if (
          this._primarySSEManagerInstanceId === (data.payload as SSEMDestroyPayload).myInstanceId &&
          this.isAuthenticated
        ) {
          const subscriptions = (data.payload as SSEMDestroyPayload).eStreamSubscriptions;
          this.logger.info(`Primary instance destroyed, restarting primary discovery process`);

          const allInstances = await this.getOtherInstances();
          this.instanceList = Object.keys(allInstances).map(Number);
          if (this.instanceList.length === 0) {
            // no other instance, so we became the primary instance
            appEventDispatcher.dispatchEvent(new AppEvents.ThisInstanceSelectedAsPrimaryEvent(subscriptions));
          } else {
            // there are other instances, let's find the primary instance
            const primaryCandidate = this.instanceList.toSorted()[0];
            this.broadcast<SSEMPrimaryInstanceSelectedPayload>(
              {
                eventType: SSEManagerEvent.PrimaryInstanceSelected,
                primaryInstanceId: primaryCandidate,
              },
              {},
            );

            appEventDispatcher.dispatchEvent(new AppEvents.ThisInstanceSelectedAsPrimaryEvent(subscriptions));
          }
        } else {
          appEventDispatcher.dispatchEvent(
            new AppEvents.OtherInstanceDestroyedEvent((data.payload as SSEMDestroyPayload).myInstanceId),
          );
        }
        break;
      case SSEManagerEvent.EStreamConnectionStateUpdate:
        const { connectionState, sseSource } = data.payload as SSEMEStreamConnectionStateUpdatePayload;
        appEventDispatcher.dispatchEvent(
          new AppEvents.SSEConnectionStateUpdateEvent({
            source: sseSource,
            connectionState,
            isInitalValue: false,
          }),
        );
        break;
      case SSEManagerEvent.EStreamTrades:
        const trades = (data.payload as SSEMEStreamTradesPayload).trades;
        appEventDispatcher.dispatchEvent(new AppEvents.EStreamTradesReceivedEvent({ trades }));
        break;
      case SSEManagerEvent.LoginStarted:
        this.setPrimaryInstance((data.payload as SSEMLoginStartedPayload).primaryInstanceId);
        appEventDispatcher.dispatchEvent(new AppEvents.OtherInstanceLoginStartedEvent());
        break;
      case SSEManagerEvent.Login:
        const {
          jwtToken,
          idToken,
          eStreamSessionId,
          eStreamConnectionState,
          userStreamConnectionState,
          inventoryDetails,
          accountBalance,
          currencyExchangeRates,
        } = data.payload as SSEMLoginPayload;
        appEventDispatcher.dispatchEvent(
          new AppEvents.OtherInstanceLoggedInEvent({
            jwtToken,
            idToken,
            eStreamSessionId,
            eStreamConnectionState,
            userStreamConnectionState,
            inventoryDetails,
            accountBalance,
            currencyExchangeRates,
          }),
        );
        break;
      case SSEManagerEvent.Logout:
        appEventDispatcher.dispatchEvent(new AppEvents.OtherInstanceLoggedOutEvent());
        break;
      case SSEManagerEvent.EStreamSubscriptionSubscribe:
        appEventDispatcher.dispatchEvent(
          new AppEvents.EStreamSubscriptionChangeEvent({
            dir: 'subscribe',
            options: (data.payload as SSEMEStreamSubscriptionChangePayload).options.map(_ => ({
              ..._,
              source: `instance-${data.nbInstance}-${_.source}`,
            })),
            instanceId: data.nbInstance,
          }),
        );
        break;
      case SSEManagerEvent.EStreamSubscriptionUnsubscribe:
        appEventDispatcher.dispatchEvent(
          new AppEvents.EStreamSubscriptionChangeEvent({
            dir: 'unsubscribe',
            options: (data.payload as SSEMEStreamSubscriptionChangePayload).options,
            instanceId: data.nbInstance,
          }),
        );
        break;
      case SSEManagerEvent.UserStreamMessage:
        const { eventType: _eventType, ...message } = data.payload as SSEMUserStreamMessagePayload;
        appEventDispatcher.dispatchEvent(new AppEvents.UserStreamMessageEvent(message, true));
        break;
      case SSEManagerEvent.InstrumentStoreRequest:
        // answer for instrument store request if we are the primary instance
        if (this.instanceId === this._primarySSEManagerInstanceId) {
        }
        this.broadcast<SSEMInstrumentStoreResponsePayload>(
          {
            eventType: SSEManagerEvent.InstrumentStoreResponse,
            store: instrumentStore.instrumentStoreItemsCopy,
          },
          { responseId: messageId },
        );
        break;
      case SSEManagerEvent.UpdateUserActivity:
        appEventDispatcher.dispatchEvent(new AppEvents.UpdateUserActivityEvent());
        break;
    }
  }

  private setPrimaryInstance(instanceId: number, isByConsensus = false) {
    this.logger.info(
      `Primary instance ${isByConsensus ? 'agreed' : 'selected'}: ${instanceId}${
        instanceId === this.instanceId ? ' (me)' : ''
      }`,
    );
    this._primarySSEManagerInstanceId = instanceId;

    if (this.instanceId === instanceId) {
      appEventDispatcher.dispatchEvent(new AppEvents.ThisInstanceSelectedAsPrimaryEvent([]));
    } else {
      // TODO start a timer to check if the primary instance is still alive
    }
    this.primaryInstanceSelected = true;

    this.updateDebugData();
  }

  broadcastEStreamMessage(trades: Array<EStreamTradeDTO> | EStreamTradeDTO) {
    this.broadcast<SSEMEStreamTradesPayload>(
      {
        eventType: SSEManagerEvent.EStreamTrades,
        trades,
      },
      {},
    );
  }

  broadcastConnectionState(sseSource: SSESource, connectionState: ConnectionState) {
    this.broadcast<SSEMEStreamConnectionStateUpdatePayload>(
      {
        eventType: SSEManagerEvent.EStreamConnectionStateUpdate,
        connectionState,
        sseSource,
      },
      {},
    );
  }

  broadcastLogin(props: MultiBrowserNetBrokerConfig) {
    this.broadcast<SSEMLoginPayload>(
      {
        eventType: SSEManagerEvent.Login,
        ...props,
      },
      {},
    );
  }

  broadcastLogout() {
    this.broadcast<SSEManagerBasePayload>(
      {
        eventType: SSEManagerEvent.Logout,
      },
      {},
    );
  }

  broadcastSubscribeToInstruments(options: Array<SubscribeOptions>) {
    this.broadcast<SSEMEStreamSubscriptionChangePayload>(
      {
        eventType: SSEManagerEvent.EStreamSubscriptionSubscribe,
        options,
      },
      {},
    );
  }

  broadcastUnsubscribeFromInstruments(options: Array<SubscribeOptions>) {
    this.broadcast<SSEMEStreamSubscriptionChangePayload>(
      {
        eventType: SSEManagerEvent.EStreamSubscriptionUnsubscribe,
        options,
      },
      {},
    );
  }

  broadcastUserStreamMessage(message: UserStreamMessage) {
    this.broadcast<SSEMUserStreamMessagePayload>(
      {
        eventType: SSEManagerEvent.UserStreamMessage,
        ...message,
      },
      {},
    );
  }

  takeOverPrimaryInstance() {
    this.logger.info(`Taking over primary instance because of a login`);
    this.broadcast<SSEMLoginStartedPayload>(
      {
        eventType: SSEManagerEvent.LoginStarted,
        primaryInstanceId: this.instanceId,
      },
      {},
    );
    this.setPrimaryInstance(this.instanceId, false);
  }

  broadcastUpdateUserActivity() {
    this.broadcast<SSEManagerBasePayload>(
      {
        eventType: SSEManagerEvent.UpdateUserActivity,
      },
      {},
    );
  }

  private updateDebugData() {
    this.addDebugObject(`sseManager`, {
      instanceId: this.instanceId,
      primarySSEManagerInstanceId: this.primarySSEManagerInstanceId,
      primaryInstanceSelected: this.primaryInstanceSelected,
      otherInstances: this.instanceList,
    });
  }

  private addDebugObject<T>(key: string, value: T) {
    store.set(debugObjectsAtom, { [key]: value });
  }
}

export const sseManager = new SSEManager();
